/**
 * Tagling — cart lock guard
 *
 * The Tagling membership is bundled with the tag: it's added automatically on
 * add-to-cart and is never sold on its own (see sections/tagling-product.liquid).
 * The cart UI already disables its quantity controls and hides its remove button
 * (see snippets/cart-products.liquid), which stops a customer changing it by hand.
 *
 * This guard backs that up for the paths the UI can't cover, holding three invariants:
 *   1. The membership quantity is always exactly 1.
 *   2. The membership never lingers without the tag — if the tag is removed, the
 *      membership goes too, so the customer is never stuck with a line they can't
 *      delete.
 *   3. The cart never holds more than 3 tags in total. Orders are capped at 3
 *      tags full stop — shipping rates only exist for 1–3 tags (each tag weighs
 *      10g; 0–10g A$8, 11–20g A$11, 21–30g A$14, nothing above 30g), so a cart
 *      with 4+ tags cannot check out at all. This guard trims the excess here so
 *      the customer sees a working cart instead of a dead end at the shipping step.
 *      It also strips the legacy "Shipping" line-item property that older carts
 *      carry from the retired A$16 bulk-rate note.
 *
 * It reacts to Horizon's `cart:update` event, corrects the cart through the AJAX
 * Cart API, then re-dispatches `cart:update` with freshly rendered sections so the
 * drawer, cart page, and header count refresh the way they do for any other cart
 * change. It fails open: any network error leaves the cart untouched.
 *
 * Note: this is a UX guard, not a security boundary. The real enforcement is
 * server-side: with no shipping rate above 30g, Shopify's checkout refuses any
 * order of 4+ tags regardless of how the cart was built.
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const MEMBERSHIP_HANDLE = 'tagling-goldmembership';
const TAG_HANDLE = 'tagling-tags';
const SOURCE = 'tagling-cart-lock';

// Keep in sync with the weight-tiered rates in Shopify admin (Settings →
// Shipping and delivery → General profile): 1 tag A$8, 2 tags A$11, 3 tags A$14,
// and no rate above 3 tags — the cap below mirrors that hard stop.
const MAX_TAGS = 3;
// Retired 2026-08-13 with the A$16 bulk rate; still stripped from older carts.
const LEGACY_NOTE_NAME = 'Shipping';

let enforcing = false;

/**
 * Is this cart line the bundled membership? It's always added with a subscription
 * selling plan; the handle is a fallback in case the plan is ever changed.
 * @param {{ selling_plan_allocation: object | null, handle: string }} item
 * @returns {boolean}
 */
function isMembership(item) {
  return item.selling_plan_allocation != null || item.handle === MEMBERSHIP_HANDLE;
}

/** Section ids of every cart-items component currently on the page. */
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

/**
 * Membership invariants (1 and 2): returns the single change needed, or null.
 * @param {Array<object>} items
 * @returns {{ id: string, quantity: number } | null}
 */
function membershipChange(items) {
  let membershipKey = null; // line-item key — targets the line even if indexes shift
  let membershipQuantity = 0;
  let hasOtherItem = false;

  for (const item of items) {
    if (isMembership(item)) {
      membershipKey = item.key;
      membershipQuantity = item.quantity;
    } else {
      hasOtherItem = true;
    }
  }

  if (membershipKey == null) return null; // no membership in the cart — nothing to do

  if (!hasOtherItem) {
    return { id: membershipKey, quantity: 0 }; // tag is gone → drop the orphaned membership
  }
  if (membershipQuantity !== 1) {
    return { id: membershipKey, quantity: 1 }; // there should only ever be one membership per cart
  }
  return null;
}

/**
 * Tag-cap invariant (3): the cart holds at most MAX_TAGS tags across all tag
 * lines. Excess is trimmed from the most recently added lines first, dropping a
 * line entirely if it goes to zero. Also strips the retired bulk-rate note from
 * any line still carrying it. Each change replaces the line's full property set,
 * so existing properties (e.g. "Pet name") are kept.
 * @param {Array<object>} items
 * @returns {Array<{ id: string, quantity: number, properties: object }>}
 */
function tagCapChanges(items) {
  const tagLines = items.filter((item) => item.handle === TAG_HANDLE);
  let excess = tagLines.reduce((sum, item) => sum + item.quantity, 0) - MAX_TAGS;

  const changes = [];
  for (let i = tagLines.length - 1; i >= 0; i--) {
    const item = tagLines[i];
    let quantity = item.quantity;
    if (excess > 0) {
      const trim = Math.min(quantity, excess);
      quantity -= trim;
      excess -= trim;
    }

    const properties = { ...(item.properties || {}) };
    const hadLegacyNote = LEGACY_NOTE_NAME in properties;
    delete properties[LEGACY_NOTE_NAME];

    if (quantity !== item.quantity || hadLegacyNote) {
      changes.push({ id: item.key, quantity, properties });
    }
  }
  return changes;
}

async function enforce() {
  if (enforcing) return;
  enforcing = true;

  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );
    const items = cart.items || [];

    const changes = [];
    const membership = membershipChange(items);
    if (membership) changes.push(membership);
    changes.push(...tagCapChanges(items));

    if (changes.length === 0) return; // invariants already hold

    const sections = cartSectionIds();
    const changeUrl = (window.Theme && Theme.routes && Theme.routes.cart_change_url) || '/cart/change.js';

    // Each change targets a distinct line by key, so applying them sequentially
    // from one cart snapshot is safe; the last response has the final cart state.
    let updated = null;
    for (const change of changes) {
      updated = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...change,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((response) => response.json());
    }

    // Re-render the cart the way Horizon's own components do. Tagging the event with
    // our source lets the listener below skip it, so this never loops.
    document.dispatchEvent(
      new CartUpdateEvent(updated, SOURCE, {
        source: SOURCE,
        sections: updated.sections,
        itemCount: updated.item_count,
      })
    );
  } catch (error) {
    // Fail open — a guard that breaks the cart is worse than one that no-ops.
    console.warn('[tagling-cart-lock] skipped:', error);
  } finally {
    enforcing = false;
  }
}

if (!window.__taglingCartLock) {
  window.__taglingCartLock = true;
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    // Ignore the event we dispatch ourselves to avoid re-entrancy.
    if (event?.detail?.data?.source === SOURCE) return;
    enforce();
  });
  // Bring an already-mutated cart in line on load — covers changes made on a
  // previous page (or via direct Cart API calls) where no cart:update fires here.
  enforce();
}
